/* eslint-disable @typescript-eslint/no-explicit-any -- U2000Input* attend FormStateHandlers générique large */
import type { TFunction } from 'i18next';
import U2000InputDecimal from '../../../u2000Core/components/formControls/input/U2000InputDecimal';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import U2000CheckBox from '../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../U3202';
import type { U3202OrgProviderConfigFormData, U3202ParameterSchemaDefinition } from '../../U3202Service';

interface U3202ModelParameterFormProps {
  formStateHandlers: U2000FormStateHandlers<U3202OrgProviderConfigFormData>;
  parameterSchema: U3202ParameterSchemaDefinition | null;
  valuesFieldPrefix: string;
  readonly: boolean;
  ignoreFormReadState?: boolean;
  t: TFunction;
  overriddenKeys?: string[];
  ns?: string;
}

function paramLabel(t: TFunction, key: string): string {
  const i18nKey = `param.${key}`;
  const translated = t(i18nKey, { ns: NS, defaultValue: '' });
  if (translated) {
    return translated;
  }
  return key.replace(/_/g, ' ');
}

function U3202ModelParameterForm({
  formStateHandlers,
  parameterSchema,
  valuesFieldPrefix,
  readonly,
  ignoreFormReadState = false,
  t,
  overriddenKeys = [],
  ns = NS,
}: U3202ModelParameterFormProps) {
  if (!parameterSchema || Object.keys(parameterSchema).length === 0) {
    return <p className="text-sm text-gray-500">{t('lblNoParameters', { ns })}</p>;
  }

  const keys = Object.keys(parameterSchema).sort();
  const overriddenSet = new Set(overriddenKeys);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {keys.map((key) => {
        const field = parameterSchema[key];
        const fieldName = `${valuesFieldPrefix}.${key}`;
        const label = paramLabel(t, key);
        const wrapperClass = overriddenSet.has(key) ? 'rounded-md ring-1 ring-amber-300 p-1' : '';

        if (field.type === 'boolean') {
          return (
            <div key={key} className={wrapperClass}>
              <U2000CheckBox
                formStateHandlers={formStateHandlers as any}
                fieldName={fieldName}
                label={label}
                readonly={readonly}
                ignoreFormReadState={ignoreFormReadState}
              />
            </div>
          );
        }

        if (field.type === 'string') {
          return (
            <div key={key} className={wrapperClass}>
              <U2000InputText
                formStateHandlers={formStateHandlers as any}
                fieldName={fieldName}
                label={label}
                readonly={readonly}
                ignoreFormReadState={ignoreFormReadState}
              />
            </div>
          );
        }

        const decimalDigits = field.type === 'integer' ? 0 : 2;
        return (
          <div key={key} className={wrapperClass}>
            <U2000InputDecimal
              formStateHandlers={formStateHandlers as any}
              fieldName={fieldName}
              label={label}
              readonly={readonly}
              ignoreFormReadState={ignoreFormReadState}
              decimalDigits={decimalDigits}
              allowNegative={field.min !== undefined ? field.min < 0 : false}
            />
          </div>
        );
      })}
    </div>
  );
}

export default U3202ModelParameterForm;
